import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { format } from 'date-fns'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import CheckboxField from 'app/frontend/base/components/form-fields/CheckboxField'
import DateField from 'app/frontend/base/components/form-fields/DateField'
import NumberField from 'app/frontend/base/components/form-fields/NumberField'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import TextField from 'app/frontend/base/components/form-fields/TextField'
import TimeField from 'app/frontend/base/components/form-fields/TimeField'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useInstances } from 'app/frontend/base/config/queries'
import { activityTypes } from 'app/frontend/types/custom/ActivityType'
import { difficulties } from 'app/frontend/types/custom/Difficulty'
import { eventParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import { eventSelectionStatuses } from 'app/frontend/types/custom/EventSelectionStatus'
import { useQueryClient } from '@tanstack/react-query'
import { Form } from '@/components/ui/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormEntityContext } from 'app/frontend/base/config/context/forms'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { FormGroup } from '@/components/ui/form'
import { transformOptions } from 'app/frontend/base/components/form-fields/SettingField'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { InfoIcon, Trash2 } from 'lucide-react'
import { eventStatuses } from 'app/frontend/types/custom/EventStatus'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import { toast } from 'sonner'

export const getAddEventFormSchema = ({
  requireCustomTitle,
}: {
  requireCustomTitle: boolean
}) =>
  z
    .object({
      date: z.string().min(1),
      startTime: z.string().min(1),
      finishTime: z.string().min(1),
      optional: z.boolean(),
      activityType: z.enum(activityTypes),
      instanceId: z.string().or(z.number()).nullable(),
      difficulty: z.enum(difficulties),
      title: z.string().nullable(),
      autoAddParticipants: z.boolean(),
      lockHoursBeforeStart: z.number().or(z.string()).nullable(),
      defaultSignupStatus: z.enum(eventParticipantStatuses).nullable(),
      defaultSelectionStatus: z.enum(eventSelectionStatuses).nullable(),
      guestPolicy: z.string().nullable(),
      status: z.enum(eventStatuses),
    })
    .superRefine((values, ctx) => {
      if (
        requireCustomTitle &&
        values.activityType === 'custom' &&
        !values.title?.trim()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: ['title'],
        })
      }
    })

export type AddEventFormValues = z.infer<
  ReturnType<typeof getAddEventFormSchema>
>

type CreateEventFormValues = AddEventFormValues & {
  id?: number
}

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  hideDateField?: boolean
  hideAutoAddParticipants?: boolean
  hideActivityFields?: boolean
  alwaysShowTitleField?: boolean
  submitLabel?: string
  title?: string
  defaultValues?: Partial<AddEventFormValues>
  onSubmit?: (values: AddEventFormValues) => Promise<void> | void
  showUpdateSuccessToast?: boolean
  showStatusControls?: boolean
  onDeleteEvent?: (values: AddEventFormValues) => Promise<void> | void
}

interface AddEventDateTimeFieldsProps {
  hideDateField?: boolean
}

const AddEventDateTimeFields = ({
  hideDateField,
}: AddEventDateTimeFieldsProps) => {
  const { team } = useTeam()
  const { t } = useTranslation()

  return (
    <FormGroup>
      {hideDateField ? null : (
        <DateField
          name='date'
          layout='stacked'
          customLabel={t('dashboard.team.events.add-event-dialog.date')}
          customPlaceholder='dashboard.team.events.add-event-dialog.select-date'
          customDescription={t(
            'dashboard.team.events.add-event-dialog.timezone',
            { timezone: team.timezone },
          )}
        />
      )}

      <TimeField
        name='startTime'
        layout='stacked'
        className='w-auto'
        customLabel={t('dashboard.team.events.add-event-dialog.start-time')}
      />

      <div className='mt-8'>-</div>

      <TimeField
        name='finishTime'
        layout='stacked'
        className='w-auto'
        customLabel={t('dashboard.team.events.add-event-dialog.end-time')}
      />
    </FormGroup>
  )
}

interface AddEventDialogContentProps {
  hideDateField?: boolean
  hideAutoAddParticipants?: boolean
  hideActivityFields?: boolean
  title?: string
  showStatusControls?: boolean
  onDeleteClick?: () => void
}

const AddEventDialogContent = ({
  hideDateField,
  hideAutoAddParticipants,
  hideActivityFields,
  title,
  showStatusControls,
  onDeleteClick,
}: AddEventDialogContentProps) => {
  const { t } = useTranslation()
  const { team } = useTeam()

  const { data: instances } = useInstances()
  const guestPolicyOptions = team.settings.raidsAllowGuests.options ?? []
  const instanceOptions = useMemo(() => {
    return (
      instances?.map((instance) => ({
        label: instance.name,
        value: instance.id,
        sortLabel: '',
      })) ?? []
    )
  }, [instances])

  const form = useFormContext()
  const activityType = form.watch('activityType')

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {title ?? t('dashboard.team.events.add-event-dialog.title')}
        </DialogTitle>
      </DialogHeader>

      <div className='mt-6'>
        <AddEventDateTimeFields hideDateField={hideDateField} />

        <FormGroup className='mt-2'>
          <CheckboxField
            name='optional'
            layout='inline'
            customLabel={
              <span className='flex items-center gap-1'>
                {t('dashboard.team.events.add-event-dialog.optional')}
                <Tooltip>
                  <TooltipTrigger>
                    <InfoIcon className='w-4 h-4 opacity-70' />
                  </TooltipTrigger>
                  <TooltipContent>
                    Optional events do not count for attendance statistics
                  </TooltipContent>
                </Tooltip>
              </span>
            }
            customDescription=''
          />

          {hideAutoAddParticipants ? null : (
            <CheckboxField
              name='autoAddParticipants'
              layout='inline'
              customLabel={
                <span className='flex items-center gap-1'>
                  {t(
                    'dashboard.team.events.add-event-dialog.auto-add-participants',
                  )}
                  <Tooltip>
                    <TooltipTrigger>
                      <InfoIcon className='w-4 h-4 opacity-70' />
                    </TooltipTrigger>
                    <TooltipContent>
                      Every user will have to explicitly sign up to appear in
                      the event, unless this is checked
                    </TooltipContent>
                  </Tooltip>
                </span>
              }
              customDescription=''
            />
          )}
        </FormGroup>

        {hideActivityFields ? null : (
          <SelectField
            options={activityTypes}
            name='activityType'
            layout='stacked'
            inModal={true}
            className='mb-2'
            customLabel={t(
              'dashboard.team.events.add-event-dialog.activity-type',
            )}
          />
        )}

        {!hideActivityFields && activityType === 'raids' ? (
          <FormGroup>
            <SelectField
              name='instanceId'
              options={instanceOptions}
              transformOptions={false}
              layout='stacked'
              inModal={true}
              customLabel={t('dashboard.team.events.add-event-dialog.instance')}
              customPlaceholder={t(
                'dashboard.team.events.add-event-dialog.current-season',
              )}
              isClearable
              onChange={(value) => form.setValue('instanceId', value ?? null)}
            />
            <SelectField
              name='difficulty'
              options={difficulties}
              layout='stacked'
              className='w-52'
              inModal={true}
              customLabel={t(
                'dashboard.team.events.add-event-dialog.difficulty',
              )}
            />
          </FormGroup>
        ) : null}

        {showStatusControls ? (
          <FormGroup>
            <SelectField
              name='status'
              options={[
                {
                  label: 'Planned',
                  value: 'planned',
                  className: 'text-success-foreground',
                },
                {
                  label: 'Locked',
                  value: 'locked',
                  description:
                    'non-admin users cannot change their signup status for locked events',
                  className: 'text-warning-foreground',
                },
                {
                  label: 'Cancelled',
                  value: 'cancelled',
                  description:
                    'cancelled events do not count for attendance statistics',
                  className: 'text-destructive-foreground',
                },
              ]}
              transformOptions={false}
              inModal={true}
              className='w-full'
              customLabel='Status'
            />
            <Button
              type='button'
              variant='outline'
              size='icon'
              className='mt-[21px] w-10'
              onClick={onDeleteClick}
            >
              <Trash2 className='h-4 w-4 text-destructive-foreground' />
            </Button>
          </FormGroup>
        ) : null}

        <TextField
          name='title'
          layout='stacked'
          className='mt-2'
          customLabel={t('dashboard.team.events.add-event-dialog.title-field')}
          customPlaceholder={
            activityType === 'custom' ? 'Title' : 'Custom title'
          }
        />

        <Accordion type='single' collapsible className='mt-2'>
          <AccordionItem value='advanced-options'>
            <AccordionTrigger className='text-sm'>
              {t('dashboard.team.events.add-event-dialog.advanced-options')}
            </AccordionTrigger>
            <AccordionContent>
              <NumberField
                name='lockHoursBeforeStart'
                layout='box'
                customPlaceholder={''}
                customLabel={t(
                  'dashboard.team.events.add-event-dialog.lock-hours-before-start',
                )}
                customDescription={t('settings.raidsAutoLock.description')}
              />
              <FormGroup>
                <SelectField
                  name='defaultSignupStatus'
                  options={eventParticipantStatuses}
                  isClearable
                  customPlaceholder={'Use team default'}
                  customLabel={t(
                    'dashboard.team.events.add-event-dialog.default-signup-status',
                  )}
                />
                <SelectField
                  name='defaultSelectionStatus'
                  options={eventSelectionStatuses}
                  isClearable
                  customPlaceholder={'Use team default'}
                  customLabel={t(
                    'dashboard.team.events.add-event-dialog.default-selection-status',
                  )}
                />
              </FormGroup>

              <SelectField
                name='guestPolicy'
                options={transformOptions(
                  t,
                  'raidsAllowGuests',
                  guestPolicyOptions,
                )}
                transformOptions={false}
                isClearable
                customPlaceholder={'Use team default'}
                customLabel={t(
                  'dashboard.team.events.add-event-dialog.guest-policy',
                )}
                customDescription={t('settings.raidsAllowGuests.description')}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </>
  )
}

const getDefaultEventValues = (team: wowaudit.Team): AddEventFormValues => {
  return {
    date: format(new Date(), 'yyyy-MM-dd'),
    startTime: '20:00',
    finishTime: '23:00',
    optional: false,
    activityType: 'raids',
    instanceId: null,
    difficulty: 'mythic',
    title: null,
    autoAddParticipants: true,
    lockHoursBeforeStart: team.settings.raidsAutoLock.value ?? null,
    defaultSignupStatus: null,
    defaultSelectionStatus: null,
    guestPolicy: null,
    status: 'planned',
  }
}

const AddEventDialog = ({
  open,
  setOpen,
  hideDateField,
  hideAutoAddParticipants,
  hideActivityFields,
  submitLabel,
  title,
  defaultValues,
  onSubmit,
  showUpdateSuccessToast = false,
  showStatusControls,
  onDeleteEvent,
}: Props) => {
  const { team } = useTeam()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const initialValues = useMemo(
    () => ({
      ...getDefaultEventValues(team),
      ...defaultValues,
    }),
    [team, defaultValues],
  )
  const localFormSchema = useMemo(
    () =>
      getAddEventFormSchema({
        requireCustomTitle: !onSubmit,
      }),
    [onSubmit],
  )
  const form = useForm<AddEventFormValues>({
    resolver: zodResolver(localFormSchema),
    defaultValues: initialValues,
  })

  useEffect(() => {
    form.reset(initialValues)
  }, [form, initialValues, open])

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent onInteractOutside={(e) => e.preventDefault()}>
        {onSubmit ? (
          <FormEntityContext.Provider
            value={{ entityType: 'event', i18nPrefix: 'forms.fields' }}
          >
            <Form {...form}>
              <div>
                <AddEventDialogContent
                  hideDateField={hideDateField}
                  hideAutoAddParticipants={hideAutoAddParticipants}
                  hideActivityFields={hideActivityFields}
                  title={title}
                  showStatusControls={showStatusControls}
                  onDeleteClick={() => setIsDeleteConfirmOpen(true)}
                />
                <div className='flex justify-end mt-6 gap-4 items-center'>
                  <Button
                    type='button'
                    size='sm'
                    variant='outline'
                    onClick={() => setOpen(false)}
                  >
                    {t('shared.cancel')}
                  </Button>
                  <Button
                    type='button'
                    size='sm'
                    loading={form.formState.isSubmitting}
                    onClick={form.handleSubmit(async (values) => {
                      await onSubmit(values)
                      setOpen(false)
                      if (showUpdateSuccessToast) {
                        toast.success(t('dashboard.team.events.updated'))
                      }
                    })}
                  >
                    {submitLabel ?? t('shared.continue')}
                  </Button>
                </div>
                <ConfirmDialog
                  open={isDeleteConfirmOpen}
                  title='Delete event?'
                  description='This event will be deleted permanently. If it was generated automatically, it will not be replaced by a new event.'
                  onCancel={() => setIsDeleteConfirmOpen(false)}
                  onContinue={form.handleSubmit(async (values) => {
                    if (!onDeleteEvent) return
                    await onDeleteEvent({ ...values, status: 'deleted' })
                    await Promise.all([
                      queryClient.invalidateQueries({
                        queryKey: ['teamEvents', { id: team.id }],
                      }),
                      queryClient.invalidateQueries({
                        queryKey: ['teamEvent'],
                      }),
                    ])
                    setIsDeleteConfirmOpen(false)
                    setOpen(false)
                    navigate(`${team.path}/events`)
                  })}
                  variant='destructive'
                />
              </div>
            </Form>
          </FormEntityContext.Provider>
        ) : (
          <FormWrapper<CreateEventFormValues>
            key={team.id}
            formSchema={localFormSchema}
            entityType='event'
            parentEntityType='team'
            parentId={team.id}
            entity={initialValues}
            showSuccessToast={false}
            onSave={async (_form, createdEvent) => {
              await queryClient.invalidateQueries({
                queryKey: ['teamEvents', { id: team.id }],
              })
              setOpen(false)
              toast.success(t('dashboard.team.events.created'), {
                action: {
                  label: t('dashboard.team.events.view-event'),
                  onClick: () =>
                    navigate(`${team.path}/events/${createdEvent.id}`),
                },
              })
            }}
            submitButtons={({ isPending }) => (
              <>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={() => setOpen(false)}
                >
                  {t('shared.cancel')}
                </Button>
                <Button type='submit' size='sm' loading={isPending}>
                  {submitLabel ?? t('dashboard.team.events.add-event')}
                </Button>
              </>
            )}
          >
            <AddEventDialogContent
              hideDateField={hideDateField}
              hideAutoAddParticipants={hideAutoAddParticipants}
              hideActivityFields={hideActivityFields}
              title={title}
              showStatusControls={showStatusControls}
            />
          </FormWrapper>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default AddEventDialog
